import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authAPI } from '../services/api';
import { setUserContext, addBreadcrumb } from '../utils/sentry';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = Cookies.get('access_token');
    if (token) {
      // Verify token and get user data
      authAPI
        .getCurrentUser()
        .then((userData) => {
          setUser(userData);
          // Set user context in Sentry
          setUserContext(userData);
          addBreadcrumb('User restored from token', 'auth', 'info');
        })
        .catch(() => {
          // Token is invalid, remove it
          Cookies.remove('access_token');
          addBreadcrumb('Token invalid, removed', 'auth', 'warning');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password);

      // Store token in cookie
      Cookies.set('access_token', data.access_token, {
        expires: 7, // 7 days
        secure: false, // Set to true in production with HTTPS
        sameSite: 'strict',
      });

      setUser(data.user);
      // Set user context in Sentry
      setUserContext(data.user);
      addBreadcrumb('User logged in', 'auth', 'info', { userId: data.user.id });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const register = async (userData) => {
    try {
      const result = await authAPI.register(userData);
      return {
        success: true,
        ...result, // Include is_admin, requires_approval, etc.
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const logout = () => {
    addBreadcrumb('User logged out', 'auth', 'info', { userId: user?.id });
    Cookies.remove('access_token');
    setUser(null);
    // Clear user context in Sentry
    setUserContext(null);
  };

  const updateProfile = async (userData) => {
    try {
      const updatedUser = await authAPI.updateCurrentUser(userData);
      setUser(updatedUser);
      // Update user context in Sentry
      setUserContext(updatedUser);
      addBreadcrumb('User profile updated', 'auth', 'info', { userId: updatedUser.id });
      return { success: true };
    } catch (error) {
      addBreadcrumb('Profile update failed', 'auth', 'error', { error: error.message });
      return {
        success: false,
        error: error.response?.data?.detail || 'Profile update failed',
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
